import PromiseValue from 'promise-value';

import { getId, getName, getType, nonce } from '../../base/data/DataClass';
import GreenTag from '../../base/data/GreenTag';
import KStatus from "../../base/data/KStatus";
import { getDataItem, getDataList, publish } from "../../base/plumbing/Crud";
import DataStore, { getListPath } from '../../base/plumbing/DataStore';
import { debounced, space } from '../../base/utils/miscutils';
import Login from "../../base/youagain";
import C from '../../C';
import ActionMan from '../../plumbing/ActionMan';
import XId from '../../base/data/XId';
import { assMatch, assert } from '../../base/utils/assert';
import SearchQuery from '../../base/searchquery';
import List from '../../base/data/List';
import Impact from '../../base/data/Impact';
import Campaign from '../../base/data/Campaign';
import ImpactDebit from '../../base/data/ImpactDebit';
import { modifyPage } from '../../base/plumbing/glrouter';


/**
 * Green Ad Tag creation: If the user has only one agency and/or advertiser available,
 * apply them to the given GreenTag.
 * @param {GreenTag} tag The tag to set up. Edited in-place.
 */
export const setTagPropsFromUser = (tag) => {
	// load user
	let pvUser = getDataItem({type: 'User', id: Login.getId(), status: KStatus.PUBLISHED, swallow: true});
	PromiseValue.then(pvUser, user => {
		['agencyId', 'vertiser'].forEach(prop => {
			if (tag[prop] || !user[prop]) return;
			tag[prop] = user[prop];
		});
	});
};


/** Shorthand for getDataItem with status PUB_OR_DRAFT and swallow errors */
export const gdi = (type, id) => (id && id !== 'unset') ? getDataItem({type, id, status: KStatus.PUB_OR_DRAFT, swallow: true}) : {};


/**
 * Create, or save changes to, a green tag.
 * Specifically avoiding SavePublishDeleteEtc because the green tag page is intended for semi-public use,
 * and the published/draft and autosave paradigms are a hotspot for confusing behaviours.
 */
export const saveTag = (item, navigateAfter = true) => {
	const type = getType(item);
	const path = DataStore.getDataPath(item);
	let afterPublish; // Action for after publish completes

	// Saving new tag?
	if (getId(item) === 'new') {
		item = new GreenTag(item); // Create copy
		item.id = nonce(8); // ...and give it a real ID
		afterPublish = () => {
			DataStore.invalidateList(type);
			DataStore.setValue(path, null); // Delete provisional version on successful publish
		};
	}

	// Do it
	return ActionMan.publishEdits(type, item.id, item).then(res => {
		if (afterPublish) afterPublish(); // ...and execute follow-up actions
		if (navigateAfter) modifyPage(['green', item.id]); // Return to view mode (on the new tag ID, if it's changed)
		return res;
	});
};


/**
 * Return an item's display name if possible, or fall back to ID. Fetches if necessary.
 * @param {!string} type 
 * @param {?string} id 
 * @returns {!string} name or id (or null if no id)
 */
export const label = (type, id) => {
	if (!id) return null;
	let pvItem = gdi(type, id);
	if (!pvItem.value) return id;
	return getName(pvItem.value) || id;
};


/**
 * @deprecated Made sense when master campaigns were a thing - currently unused.
 * Chain-get the campaign for e.g. an agency or brand.
 * @param {String} thingId ID of the agency, brand, etc in question
 * @param {String} thingType Type of the thing (eg Agency, Advertiser)
 * @param {?String} thingMember Where on the fetched thing the campaign ID is found (default: thing.campaign)
 * @returns PromiseValue(Campaign|false) never null Hack WARNING might not be the "true" PromiseValue -- do not chain on this
 */
export const getCampaignFor = (thingId, thingType, thingMember = 'campaign') => {
	if (!thingId) return PromiseValue.pending();

	const pvThing = gdi(thingType, thingId);
	if (!pvThing.resolved) return pvThing; // HACK

	let cid = pvThing?.value?.[thingMember];
	if (!cid) return new PromiseValue(false);

	return gdi(C.TYPES.Campaign, cid);
};


/** For CSV exports - resolve item IDs (eg tag agency/brand/campaign) to name + ID */
export const itemAccessor = (type, id) => {
	const pvObj = id && gdi(type, id);
	return space(pvObj?.value?.name, `[G-L ${id}]`);
};


/** For CSV exports - stable layout and more useful column info than the default generated schema */
export const greenTagCsvColumns = [
	{Header: 'Tag', accessor: 'name'},
	{Header: 'link', accessor: row => `${window.location.protocol}//${window.location.host}/#green/${row.id}`},
	{Header: 'Created by', accessor: row => row.oxid && XId.id(row.oxid)},
	{Header: 'Created at', accessor: 'created'},
	{Header: 'Ad format', accessor: 'format'},
	{Header: 'Creative Type', accessor: row => row.creativeURL ? 'URL' : row.creativeHtml ? 'Tag' : 'None' },
	{Header: 'Creative', accessor: row => row.creativeURL || row.creativeHtml},
	{Header: 'Agency', accessor: row => itemAccessor(C.TYPES.Agency, row.agencyId)},
	{Header: 'Brand', accessor: row => itemAccessor(C.TYPES.Advertiser, row.vertiser)},
	{Header: 'Campaign', accessor: row => itemAccessor(C.TYPES.Campaign, row.campaign)},
];



// /** Matches impact names which can be used for the "carbon/trees/coral" selector. Does NOT include carbon */
// const IMPACT_MODE_REGEX = /^(tree\(s\)|trees|tree|coral)?$/;
// /** Predicate function for finding mode marker offsets. Does NOT include carbon. */
// const modeImpactPredicate = o => o?.rate && !o.n && o.name.match(IMPACT_MODE_REGEX);


/** Green Ad Tag field which holds an item ID for a given type*/
const propForType = {
	[C.TYPES.Campaign]: 'campaign',
	[C.TYPES.Advertiser]: 'vertiser',
	[C.TYPES.Agency]: 'agencyId'
};


/**
 * Is this data item set to buy carbon offsets only, or carbon + trees or coral?
 * @param {!String} id
 * @param {!String} type
 * @returns {PromiseValue<ImpactDebit[]>} Names of impact objects on this campaign - non-carbon first. null if loading
 */
export const getDynamicImpactDebits = (id, type) => {
	assert(id);
	assert(type);
	const prop = propForType[type];
	assert(prop, `Given type has no matching prop for impact debits: ${type}`);

	// Is there a non-carbon offset on the campaign?
	const iq = SearchQuery.setProp('(impact.dynamic:true OR impact.rate:any)', prop, id);
	const getSpec = { type: 'ImpactDebit', status: KStatus.PUBLISHED, q: iq.query };
	// Add a wart to the list path signifying "filtered to dynamic only" to avoid collision with similar list queries
	const listPath = getListPath({ ...getSpec, filtered: 'dynamic' });

	return DataStore.fetch(listPath, () => {
		// Fetch all relevant ImpactDebits from server...
		return getDataList(getSpec).promise.then(debits => {
			// ...then filter to only the dynamic ones.
			return List.hits(debits).filter(imp => Impact.isDynamic(imp.impact));
		});
	});
};


/**
 * Set a campaign to buy carbon offsets only, or carbon + trees or coral
 * @param {!Campaign} campaign The campaign. Sets campaign._impactIds as an instant transient cache.
 * @param {!String[]} impactDebitIds 
 * @param {!ImpactDebit[]} templates List of template impact objects
 */
export const setDynamicImpactDebits = (campaign, impactDebitIds, templates) => {
	let impactIds = templates.filter(impd => impactDebitIds.includes(impd.id)).map(impd => impd.id);
	campaign._impactIds = impactIds;
	// NB: use campaign.id as a fixed parameter to mark the function, as it is more stable than the Campaign object.
	let huh = debounced(setDynamicImpactDebits2, [campaign.id], [campaign, impactIds, templates], 1000, {leading: true, trailing: false});
	console.log(huh);
};


/**
 * Set a campaign to buy carbon offsets only, or carbon + trees or coral
 * @param {!String} cid campaign id. Ignored here - it is used as a marker by debounced()
 * @param {!Campaign} campaign The campaign
 * @param {!String[]} impactIds 
 * @param {!ImpactDebit[]} templates List of template impact objects
 */
const setDynamicImpactDebits2 = (cid, campaign, impactIds, templates) => {
	assMatch(campaign, Campaign);
	assMatch(impactIds, "String[]");
	assMatch(templates, "ImpactDebit[]");
	assert(templates.length, "MUST setup a default carbon-offset to use as a template");
	const impds = getDynamicImpactDebits(campaign.id, C.TYPES.Campaign).value;
	assert(impds != null);

	// what do we need to add and remove?
	const oldIds = impds.map(impd => impd.id);
	impds.forEach(impd => {
		if (impd.parent) oldIds.push(impd.parent);
	});
	const newIds = impactIds.filter(n => !oldIds.includes(n));
	// add
	newIds.forEach(nn => {
		const templateImpD = templates.find(imp => imp.id === nn);
		assert(templateImpD, impactIds);
		// Naming convention: {impact.name}4{campaign.id}
		const id = `${templateImpD.impact.name}4${campaign.id}`;
		const item = new ImpactDebit({...templateImpD, id});
		item.campaign = campaign.id;
		item.agencyId = campaign.agencyId;
		item.vertiser = campaign.vertiser;
		item.crmDeal = campaign.crm;
		console.warn('create', item, 'for', nn);
		publish({type: 'ImpactDebit', id, item, swallow: true});
	});
	// remove
	impds.forEach(impd => {
		if (impactIds.includes(impd.id)) return;
		if (impactIds.includes(impd.parent)) return;
		console.error('delete', impd, 'not in', impactIds);
		// TODO ActionMan.delete('ImpactDebit', impd.id); // FIXME
	});
};


/**
 * Get the list of Impacts which can be used as options for a Green Ad Tag's campaign
 * These can come from the brand's master campaign, the agency's master campaign, or the Good-Loop master campaign
 * 
 * FIXME if used to setup a starting value, then there's a race condition in the PVs.
 * Which could lead to the general defaut being used instead of the agency default.
 * 
 * TODO Refactor to return a single PV with promise-collect
 * 
 * @param {GreenTag} tag
 * @returns {?ImpactDebit[]} can be empty (shouldn't really happen though). null if loading!
 */
export const getImpactTemplates = tag => {
	if (!tag) return [];
	// Get dynamic ImpactDebits for the Campaign objects
	// ...Get relevant Campaign ids -- done a bit inefficiently by fetching the Campaign objects
	const pvCampaign = tag.campaign ? getDynamicImpactDebits(tag.campaign, C.TYPES.Campaign) : null;
	// TODO advertiser and agency pull in ImpactDebits from unrelated campaigns
	// We could fetch "non specific" (e.g. agency but no advertiser) dynamic ImpactTemplates. 
	// But cleaner to add a "it's a template" flag to ImpactDebit, and set it for the few templates we have in play
	const pvVertiser = tag.vertiser ? getDynamicImpactDebits(tag.vertiser, C.TYPES.Advertiser) : null;
	const pvAgency = tag.agencyId ? getDynamicImpactDebits(tag.agencyId, C.TYPES.Agency) : null;
	const pvTotal = getDynamicImpactDebits(Campaign.TOTAL_IMPACT, C.TYPES.Campaign);
	const allPVs = [
		pvCampaign,
		pvVertiser,
		pvAgency,
		pvTotal
	].filter(x => x);

	// Don't evaluate the chain until all PVs have resolved
	if (allPVs.filter(pv => !pv.resolved).length) {
		return null;
	}

	// load impacts in order
	// NB the campaignPVs are ordered such that we want the earlier ones to prevail if there's an overlap (advertiser over agency over default)
	const impactTemplate4name = {};
	allPVs.forEach(pv => {
		pv.value?.forEach(impd => {
			assert(impd.impact.name);
			let iname = impd.impact.name;
			if (impd.impact?.charity === 'none') iname += '.none'; // HACK to let a "none" through
			if (impactTemplate4name[iname]) {
				return;
			}
			impactTemplate4name[iname] = impd;
		});
	});
	let impactTemplates = Object.values(impactTemplate4name);
	return impactTemplates;
};
